import { getApiBaseUrl, getAuthHeaders } from '../config/api';

export interface AuctionAdapter {
  key: string;
  name: string;
  formats: Array<{ key: string; name: string }>;
  supportsBidSubmission: boolean;
  supportsDocumentUpload: boolean;
  behavior: string;
}

export interface AuctionItem {
  id: string;
  itemNumber: string;
  lot?: string | null;
  description: string;
  quantity: number;
  unit?: string | null;
  referenceValue: number;
  hardFloorPrice: number;
  currentBestBid?: number | null;
  ourLastBid?: number | null;
  ourPosition?: number | null;
  visibleCompetitors?: number | null;
  status: string;
  autoEnabled: boolean;
  decrementType: 'FIXED' | 'PERCENT';
  decrementValue: number;
  minIntervalSeconds: number;
  maxAutomaticBids: number;
  automaticBidCount: number;
  lastAutomaticBidAt?: string | null;
  allowClosedPhase: boolean;
  initialProposals: AuctionInitialProposal[];
}

export interface AuctionInitialProposal {
  id: string;
  companyName: string;
  amount: number;
  position?: number | null;
  isOurProposal: boolean;
  source: string;
  visibleAt: string;
}

export interface AuctionEvent {
  id: string;
  source: string;
  actorName?: string | null;
  amount?: number | null;
  status: string;
  reason: string;
  occurredAt: string;
  item?: { itemNumber: string } | null;
}

export interface AuctionDocument {
  id: string;
  targetPortal: string;
  status: string;
  requiresSignature: boolean;
  submittedAt?: string | null;
  log?: string | null;
  attachment?: { id: string; name: string; type?: string | null; url: string } | null;
}

export interface AuctionSession {
  id: string;
  title: string;
  mode: 'SIMULATION';
  adapterKey: string;
  portalName: string;
  externalReference?: string | null;
  format: 'OPEN_ITEM' | 'LOT_TOTAL' | 'OPEN_CLOSED';
  status: 'DRAFT' | 'RUNNING' | 'PAUSED' | 'FINISHED';
  phase: 'PREPARATION' | 'OPEN' | 'CLOSED_BID' | 'PAUSED' | 'CLOSED';
  disputeMode: 'OPEN' | 'OPEN_CLOSED' | 'CLOSED';
  humanApprovalRequired: boolean;
  liveSubmissionEnabled: boolean;
  items: AuctionItem[];
  events: AuctionEvent[];
  documents: AuctionDocument[];
  phaseEndsAt?: string | null;
  singleBidAt?: string | null;
  onlineCompetitors?: number | null;
  initialProposalsVisible: boolean;
  lastPortalSyncAt?: string | null;
  syncStatus: 'NOT_CONFIGURED' | 'SIMULATED' | 'CONNECTED' | 'STALE' | 'ERROR';
  syncMessage?: string | null;
  updatedAt: string;
}

export interface PortalSummary {
  id: string;
  name: string;
  url: string;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers({ ...getAuthHeaders(), ...(init.headers || {}) });
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const response = await fetch(`${getApiBaseUrl()}${path}`, { ...init, headers });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || `Erro na sala de pregão (${response.status}).`);
  return data as T;
}

export const auctionService = {
  getAdapters: () => request<AuctionAdapter[]>('/auction/adapters'),
  getSessions: (tenderId: string) => request<AuctionSession[]>(`/auction/sessions?tenderId=${encodeURIComponent(tenderId)}`),
  getPortals: () => request<PortalSummary[]>('/portals'),
  createSession: (payload: Record<string, unknown>) => request<AuctionSession>('/auction/sessions', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  configureItem: (sessionId: string, itemId: string, payload: Record<string, unknown>) => request<AuctionItem>(`/auction/sessions/${sessionId}/items/${itemId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  }),
  start: (sessionId: string) => request<AuctionSession>(`/auction/sessions/${sessionId}/start`, { method: 'POST' }),
  pause: (sessionId: string) => request<AuctionSession>(`/auction/sessions/${sessionId}/pause`, { method: 'POST' }),
  finish: (sessionId: string) => request<AuctionSession>(`/auction/sessions/${sessionId}/finish`, { method: 'POST' }),
  setPhase: (sessionId: string, phase: 'OPEN' | 'CLOSED_BID') => request<AuctionSession>(`/auction/sessions/${sessionId}/phase`, {
    method: 'POST',
    body: JSON.stringify({ phase }),
  }),
  updateOperationalState: (sessionId: string, payload: Record<string, unknown>) => request<AuctionSession>(`/auction/sessions/${sessionId}/operational-state`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  }),
  competitorBid: (sessionId: string, itemId: string, amount?: number) => request<{ session: AuctionSession }>(`/auction/sessions/${sessionId}/items/${itemId}/competitor-bid`, {
    method: 'POST',
    body: JSON.stringify(amount ? { amount } : {}),
  }),
  manualBid: (sessionId: string, itemId: string, amount: number) => request<{ session: AuctionSession }>(`/auction/sessions/${sessionId}/items/${itemId}/manual-bid`, {
    method: 'POST',
    body: JSON.stringify({ amount, humanApproved: true }),
  }),
  previewAutoBid: (sessionId: string, itemId: string) => request<{ allowed: boolean; reason: string; candidate?: number; availableAt?: string }>(`/auction/sessions/${sessionId}/items/${itemId}/auto-bid/preview`, {
    method: 'POST',
  }),
  executeAutoBid: (sessionId: string, itemId: string) => request<{ session: AuctionSession }>(`/auction/sessions/${sessionId}/items/${itemId}/auto-bid/execute`, {
    method: 'POST',
    body: JSON.stringify({ humanApproved: true }),
  }),
  runAutoCycle: (sessionId: string) => request<{ session: AuctionSession }>(`/auction/sessions/${sessionId}/auto-bid/run-cycle`, {
    method: 'POST',
    body: JSON.stringify({ humanApproved: true }),
  }),
  addDocument: (sessionId: string, attachmentId: string, requiresSignature: boolean) => request<AuctionDocument>(`/auction/sessions/${sessionId}/documents`, {
    method: 'POST',
    body: JSON.stringify({ attachmentId, requiresSignature }),
  }),
  simulateDocumentUpload: (sessionId: string, documentId: string) => request<AuctionDocument>(`/auction/sessions/${sessionId}/documents/${documentId}/simulate-upload`, {
    method: 'POST',
  }),
};
